/**
 * Demo API Handler
 * 
 * Intercepts API calls for demo users and returns mock data
 * Prevents "invalid token" errors
 */

import { isCurrentUserDemo } from './demoUserConfig';

// Mock data for demo users
const DEMO_DATA = {
  shows: [
    {
      _id: 'demo_show_1',
      name: 'Summer Festival 2024',
      date: '2024-07-15',
      venue: 'Central Park',
      city: 'New York',
      state: 'NY',
      country: 'USA',
      status: 'confirmed',
      type: 'festival',
    },
    {
      _id: 'demo_show_2',
      name: 'Album Release Party',
      date: '2024-08-20',
      venue: 'The Roxy',
      city: 'Los Angeles',
      state: 'CA',
      country: 'USA',
      status: 'confirmed',
      type: 'headliner',
    },
  ],
  contacts: [],
  catalog: [
    {
      _id: 'demo_track_1',
      title: 'Demo Song',
      artist: 'Demo Artist',
      releaseDate: '2024-01-01',
      type: 'single',
    },
  ],
  bookings: [],
  profile: {
    _id: 'demo_user_' + Date.now(),
    name: 'Demo User',
    email: 'demo@dizee.app',
    username: 'Demo User',
    professionRole: 'demo',
    isDemo: true,
  },
};

/**
 * Check if an API call should be intercepted for demo users
 */
export const shouldInterceptCall = (url) => {
  if (!isCurrentUserDemo()) return false;
  
  // For demo users, intercept ALL API calls except authentication and AI chat
  const allowedEndpoints = [
    'auth/',
    'login',
    'signup',
    'chatWithAI',           // AI chat functionality
    'getAIChatHistory',     // Load chat history
    'getCareerAdvice',      // Career advice feature
    'conversations/',       // Conversation management (rename, delete)
  ];
  
  // Don't intercept allowed endpoints
  const isAllowedCall = allowedEndpoints.some(endpoint => url.includes(endpoint));
  if (isAllowedCall) return false;
  
  // Intercept everything else for demo users
  return true;
};

/**
 * Get mock response for demo users
 */
export const getMockResponse = (url) => {
  console.log('🎭 Demo API Handler: Intercepting call to', url);
  
  if (url.includes('tm/shows') || url.includes('getBookings')) {
    return {
      data: {
        status: 200,
        message: 'Demo data loaded',
        data: DEMO_DATA.shows,
      },
    };
  }
  
  if (url.includes('getCatalog')) {
    return {
      data: {
        status: 200,
        message: 'Demo data loaded',
        data: DEMO_DATA.catalog,
      },
    };
  }
  
  if (url.includes('getContacts')) {
    return {
      data: {
        status: 200,
        message: 'Demo data loaded',
        data: DEMO_DATA.contacts,
      },
    };
  }
  
  if (url.includes('getUserArticle') || url.includes('get-user-article')) {
    return {
      data: {
        status: 200,
        message: 'Demo data loaded',
        data: {
          user: DEMO_DATA.profile,
        },
      },
    };
  }
  
  if (url.includes('getVaultData') || url.includes('get-vault-data')) {
    return {
      data: {
        status: 200,
        message: 'Demo data loaded',
        data: {
          sections: [],
          profile: DEMO_DATA.profile,
        },
      },
    };
  }
  
  // Default response for any unhandled endpoint
  console.log('⚠️ Demo API: No specific mock data for:', url, '- returning empty array');
  return {
    data: {
      status: 200,
      message: 'Demo mode - limited data available',
      data: [],
    },
  };
};

/**
 * Wrap API call with demo handling
 */
export const wrapApiCall = async (apiFunction, ...args) => {
  if (!isCurrentUserDemo()) {
    return apiFunction(...args);
  }
  
  // Extract URL from arguments (usually first arg)
  const url = typeof args[0] === 'string' ? args[0] : '';
  
  if (shouldInterceptCall(url)) {
    console.log('🎭 Returning mock data for demo user');
    return Promise.resolve(getMockResponse(url));
  }
  
  // For non-intercepted calls, try the real API but catch errors gracefully
  try {
    return await apiFunction(...args);
  } catch (error) {
    console.warn('🎭 Demo API call failed, returning empty data:', error);
    return {
      data: {
        status: 200,
        message: 'Demo mode - feature not available',
        data: null,
      },
    };
  }
};

export default {
  shouldInterceptCall,
  getMockResponse,
  wrapApiCall,
};

